import type { GuideStep } from '../types';

export const GROUPS_LAYERS_STEPS: GuideStep[] = [
  {
    id: 'groups-1',
    page: 'index',
    section: 'groups-layers',
    target: 'canvas',
    title: { sv: 'Vad är en grupp?', en: 'What is a group?' },
    body: {
      sv: 'En grupp är en visuell ram runt flera block. Blocken är fortsatt synliga och redigerbara — gruppen håller bara ihop dem visuellt.',
      en: 'A group is a visual frame around several blocks. The blocks remain visible and editable — the group just binds them visually.',
    },
    placement: 'auto',
    order: 1,
  },
  {
    id: 'groups-2',
    page: 'index',
    section: 'groups-layers',
    target: 'block',
    fallbackTargets: ['layer-card'],
    title: { sv: 'Högerklicka blocket', en: 'Right-click the block' },
    body: {
      sv: 'Markera flera block med Shift+klick. Högerklicka sedan på ett av dem för att öppna kontextmenyn.',
      en: 'Select multiple blocks with Shift+click. Then right-click one of them to open the context menu.',
    },
    advanceOn: { kind: 'contextmenu' },
    showMeHow: {
      // Triggers a real contextmenu so the user sees the menu appear —
      // this also dispatches `block:rightclick` which advances the step
      // to the next one (picking "Group"). Friendly chained demo.
      steps: [
        { selector: '[data-guide="block"]::eq(0)', action: 'rightclick', label: 'Right-click', hold: 1000, trigger: true },
      ],
      durationMs: 2500,
    },
    waitingHint: {
      sv: 'Högerklicka på ett block',
      en: 'Right-click a block',
    },
    placement: 'auto',
    order: 2,
  },
  {
    id: 'groups-2b',
    page: 'index',
    section: 'groups-layers',
    target: 'block',
    fallbackTargets: ['layer-card'],
    // The Radix menu lives in a portal outside our overlay tree — include it
    // in the spotlight so the user sees where to click next.
    includeOpenMenus: true,
    additionalHighlight: '[data-guide="ctx-group"]',
    title: { sv: 'Välj "Gruppera"', en: 'Pick "Group"' },
    body: {
      sv: 'Klicka på "Gruppera" i menyn. Du kan namnge gruppen och ändra färg efteråt i egenskapspanelen.',
      en: 'Click "Group" in the menu. You can rename it and change its color afterwards in the properties panel.',
    },
    advanceOn: { kind: 'menuItemSelected', action: 'ctx-group' },
    showMeHow: {
      // Step 1 opens the menu via synthetic contextmenu so step 2's
      // target (`ctx-group`, which only exists when the menu is open)
      // can actually be found. Only the OPENER has trigger: true — we
      // never auto-click the final "Group" item, the user must do that
      // themselves to actually group.
      steps: [
        { selector: '[data-guide="block"]::eq(0)', action: 'rightclick', label: 'Right-click', hold: 600, trigger: true },
        { selector: '[data-guide="ctx-group"]', action: 'click', label: 'Click "Group"', hold: 700 },
      ],
      durationMs: 3500,
    },
    waitingHint: {
      sv: 'Klicka på "Gruppera" i menyn',
      en: 'Click "Group" in the menu',
    },
    placement: 'auto',
    order: 2.5,
  },
  {
    id: 'groups-3',
    page: 'index',
    section: 'groups-layers',
    target: 'block',
    fallbackTargets: ['layer-card'],
    includeOpenMenus: true,
    additionalHighlight: '[data-guide="ctx-convert-to-layer"]',
    title: { sv: 'Konvertera till lager', en: 'Convert to layer' },
    body: {
      sv: 'Högerklicka på ett block och välj "Konvertera till lager". Blocket blir då ett kompakt lager-kort som du kan dubbelklicka för att kliva in i.',
      en: 'Right-click a block and pick "Convert to layer". The block becomes a compact layer card you can dive into by double-clicking.',
    },
    advanceOn: { kind: 'event', name: 'block:convertedToLayer' },
    showMeHow: {
      // Opener fires a real contextmenu so the menu actually opens and
      // `ctx-convert-to-layer` is resolvable. The click on the menu item
      // is visual only — user must perform the real click themselves.
      steps: [
        { selector: '[data-guide="block"]::eq(0)', action: 'rightclick', label: 'Right-click the block', hold: 800, trigger: true },
        { selector: '[data-guide="ctx-convert-to-layer"]', action: 'click', label: 'Click "Convert to layer"', hold: 700 },
      ],
      durationMs: 4000,
    },
    waitingHint: {
      sv: 'Välj "Konvertera till lager" i högerklicksmenyn',
      en: 'Pick "Convert to layer" from the right-click menu',
    },
    placement: 'auto',
    order: 3,
  },
  {
    id: 'groups-4',
    page: 'index',
    section: 'groups-layers',
    target: 'layer-card',
    fallbackTargets: ['canvas'],
    title: { sv: 'Lager-kortet', en: 'The layer card' },
    body: {
      sv: 'Lagret syns nu som ett kompakt kort. Dubbelklicka för att kliva in i det — nästa sektion visar hur du navigerar tillbaka med breadcrumbs.',
      en: "The layer now shows as a compact card. Double-click it to dive in — the next section shows how to navigate back using breadcrumbs.",
    },
    placement: 'auto',
    order: 4,
  },
];
